import React, { useState, useEffect, useRef } from 'react';
import {
  LISTING_FEE_CENTS,
  LISTING_FEE_PREMIUM_CENTS,
  LISTING_FEE_PREMIUM_MIN_PRICE,
  LISTING_SIDE_COMMISSION_LABEL,
  formatDollars,
  listingFeeCents,
  listingSideCommission,
} from '../../utils/pricing';
import { PRICING_DISCLAIMER } from '../../utils/disclaimers';
import './PricingPage.css';

// Published prices (see utils/pricing.js — the backend charges the same tiers).
const LISTING_FEE = formatDollars(LISTING_FEE_CENTS);
const LISTING_FEE_PREMIUM = formatDollars(LISTING_FEE_PREMIUM_CENTS);
const PREMIUM_THRESHOLD = `$${LISTING_FEE_PREMIUM_MIN_PRICE.toLocaleString('en-US')}`;

// Buyer Paperwork (an offer on a home listed elsewhere) is not built yet: an
// offer needs a cutthe6.com listing and a seller account. Its plan card, FAQ
// entry and comparison row stay here, hidden, until the flow exists.
const BUYER_PAPERWORK_LIVE = false;
const live = (item) => BUYER_PAPERWORK_LIVE || !item.buyerPaperwork;

// ─── Animated Counter ──────────────────────────────────────────────────────
function AnimatedNumber({ value, prefix = '', suffix = '', duration = 600 }) {
  const [display, setDisplay] = useState(0);
  const startRef = useRef(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const start = startRef.current;
    const end = value;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        startRef.current = end;
      }
    };

    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [value, duration]);

  return (
    <span>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

// ─── Feature Row ───────────────────────────────────────────────────────────
function CheckRow({ text, checked, featured }) {
  return (
    <li className={`pricing-feature ${checked ? 'pricing-feature--yes' : 'pricing-feature--no'} ${featured ? 'pricing-feature--featured' : ''}`}>
      <span className="pricing-feature__icon" aria-hidden="true">
        {checked ? '✓' : '—'}
      </span>
      <span>{text}</span>
    </li>
  );
}

// ─── Savings Calculator ────────────────────────────────────────────────────
const PRICE_PRESETS = [300_000, 450_000, 600_000, 800_000, 1_100_000];

function SavingsCalculator() {
  const [homePrice, setHomePrice] = useState(600_000);

  // Listing side only (utils/pricing.js): the flat fee replaces the seller's
  // listing agent, not the buyer's agent, so the combined 5–6% overstates it.
  const agentCost = listingSideCommission(homePrice);
  const ourCost = listingFeeCents(homePrice) / 100;
  const saved = agentCost - ourCost;
  const savedPct = Math.round((saved / agentCost) * 100);

  return (
    <div className="savings-calc">
      <div className="savings-calc__header">
        <h2 className="savings-calc__title">How much will you save?</h2>
        <p className="savings-calc__sub">
          Listing agents typically charge around 2.5–3% of the sale price for
          the seller&apos;s side. We charge one flat fee to list.
        </p>
      </div>

      <div className="savings-calc__presets">
        {PRICE_PRESETS.map((p) => (
          <button
            key={p}
            className={`savings-preset ${homePrice === p ? 'active' : ''}`}
            onClick={() => setHomePrice(p)}
          >
            {p >= 1_000_000 ? `$${(p / 1_000_000).toFixed(1)}M` : `$${(p / 1000).toFixed(0)}k`}
          </button>
        ))}
      </div>

      <div className="savings-calc__slider-row">
        <span className="savings-calc__label">$100k</span>
        <input
          type="range"
          min={100_000}
          max={2_000_000}
          step={10_000}
          value={homePrice}
          onChange={(e) => setHomePrice(Number(e.target.value))}
          className="savings-slider"
          aria-label="Home price"
        />
        <span className="savings-calc__label">$2M</span>
      </div>

      <div className="savings-calc__price-display" aria-live="polite">
        <AnimatedNumber value={homePrice} prefix="$" /> home
      </div>

      <div className="savings-calc__grid">
        <div className="savings-bar savings-bar--bad">
          <div className="savings-bar__label">Typical listing-agent commission (est. {LISTING_SIDE_COMMISSION_LABEL})</div>
          <div className="savings-bar__amount">
            <AnimatedNumber value={agentCost} prefix="$" />
          </div>
          <div className="savings-bar__fill savings-bar__fill--bad" style={{ width: '100%' }} />
        </div>

        <div className="savings-bar savings-bar--good">
          <div className="savings-bar__label">Cut the 6 listing fee</div>
          <div className="savings-bar__amount savings-bar__amount--good" data-testid="savings-our-cost">
            <AnimatedNumber value={ourCost} prefix="$" />
          </div>
          <div
            className="savings-bar__fill savings-bar__fill--good"
            style={{ width: `${Math.max(2, (ourCost / agentCost) * 100)}%` }}
          />
        </div>
      </div>

      <div className="savings-calc__result">
        <div className="savings-result-label">You could keep</div>
        <div className="savings-result-amount">
          <AnimatedNumber value={saved} prefix="$" />
        </div>
        <div className="savings-result-pct">
          That's about <AnimatedNumber value={savedPct} suffix="%" /> of a typical listing commission
        </div>
        <p className="savings-calc__estimate-note">
          Illustrative estimate. Commissions vary and are negotiable, and a
          listing agent provides services we don&apos;t. Any buyer&apos;s-agent
          pay is separate and not included.
        </p>
      </div>
    </div>
  );
}

// ─── Plan Data ──────────────────────────────────────────────────────────────
// Plans from the pricing sheet: free offers on cutthe6.com homes, a flat buyer
// fee for paperwork on homes listed elsewhere (hidden until it exists — see
// BUYER_PAPERWORK_LIVE), and a flat listing fee (two tiers by list price) that
// includes everything buyers get.
const PLANS = [
  {
    key: 'explorer',
    name: 'Explorer',
    tagline: 'Browse, tour, and make offers on cutthe6.com homes',
    priceLabel: 'Free',
    pricingNote: 'Free for buyers · No credit card required',
    ctaLabel: 'Browse Homes',
    ctaVariant: 'outline',
    badge: null,
    color: 'neutral',
    features: [
      { text: 'Browse all cutthe6.com listings', yes: true },
      { text: 'Save searches & favorites', yes: true },
      { text: 'Affordability calculator', yes: true },
      { text: 'Tour scheduling', yes: true },
      { text: 'Write & e-sign offers on cutthe6.com homes', yes: true, featured: true },
      { text: 'Offer status notifications', yes: true },
      { text: 'Paperwork for homes listed elsewhere', yes: false, buyerPaperwork: true },
      { text: 'List your own home', yes: false },
    ],
  },
  {
    key: 'buyer',
    buyerPaperwork: true,
    name: 'Buyer Paperwork',
    tagline: 'Prepare your own offer for a home listed elsewhere',
    // No price on the card: it is presented as part of buying on the site.
    priceText: 'Part of buying on cutthe6.com',
    pricingNote: 'Offers on cutthe6.com homes stay free',
    ctaLabel: 'Start Buying',
    ctaVariant: 'outline',
    badge: null,
    color: 'coral',
    features: [
      { text: 'Everything in Explorer', yes: true },
      { text: 'Offer paperwork for a home not listed on cutthe6.com', yes: true, featured: true },
      { text: 'Document management & e-sign', yes: true, featured: true },
      { text: 'AI offer suggestions (you decide)', yes: true },
      { text: 'Contingency & deadline tracking', yes: true },
      { text: 'Transaction journey dashboard', yes: true },
      { text: 'List your own home', yes: false },
    ],
  },
  {
    key: 'pro',
    name: 'List Your Home',
    tagline: 'Publish and manage your own for-sale-by-owner listing. Buyer tools included.',
    price: LISTING_FEE_CENTS / 100,
    priceSuffix: 'one-time',
    tierNote: `${LISTING_FEE_PREMIUM} for homes listed at ${PREMIUM_THRESHOLD} or more`,
    pricingNote: 'Flat software fee · Charged when you publish, whether or not your home sells',
    ctaLabel: 'List Your Home',
    ctaVariant: 'solid',
    badge: 'Best Value',
    color: 'navy',
    compareNote: 'vs. a typical 2.5–3% listing commission',
    features: [
      { text: 'Everything in Explorer — buyer tools included', yes: true, featured: true },
      { text: 'Listing with photos & full property details', yes: true, featured: true },
      { text: 'Seller dashboard', yes: true },
      { text: 'AI price estimate (a suggestion, not an appraisal)', yes: true },
      { text: 'Tour requests & availability control', yes: true },
      { text: 'Offer inbox & counter-offers', yes: true, featured: true },
      { text: 'Seller agreement & disclosure e-sign', yes: true },
      { text: 'Closing checklist & deadlines', yes: true },
    ],
  },
];

const VISIBLE_PLANS = PLANS.filter(live);

// ─── FAQ Data ───────────────────────────────────────────────────────────────
// Fee wording follows docs/plans/stripe-billing/08-legal-review.md §3.1: a flat
// software fee charged at listing — never "per transaction" or "at closing".
const FAQ = [
  {
    q: 'Can I really make an offer for free?',
    a: 'Yes. Offers on homes listed on cutthe6.com are free for buyers — browse, tour, and write, sign, and track your offer without a credit card.',
  },
  {
    q: 'When is the listing fee charged?',
    a: `Once, when you publish your home on cutthe6.com. It is a flat fee for using our software — ${LISTING_FEE}, or ${LISTING_FEE_PREMIUM} for homes listed at ${PREMIUM_THRESHOLD} or more. It is not a commission or a percentage of your price, it is not taken from sale proceeds, and it is owed whether or not your home sells.`,
  },
  {
    q: `Why is it ${LISTING_FEE_PREMIUM} for homes listed at ${PREMIUM_THRESHOLD} or more?`,
    a: `Homes listed at ${PREMIUM_THRESHOLD} or more use the ${LISTING_FEE_PREMIUM} plan. Your plan is set by your list price when you publish: if you paid ${LISTING_FEE} and raise your price to ${PREMIUM_THRESHOLD} or more before publishing, only the difference is due. Once your home has been published, the plan does not change. Nothing is charged at closing.`,
  },
  {
    buyerPaperwork: true,
    q: 'What is Buyer Paperwork?',
    a: 'It is for buying a home that is not listed on cutthe6.com. You use our software to prepare, sign, and track your own offer on standard forms, and you decide every term. It is not legal advice or representation.',
  },
  {
    q: 'Where is Cut the 6 available?',
    a: 'Cut the 6 currently supports homes in Wisconsin, with more states to follow. We provide software, not legal advice — you choose whether to involve an attorney or title company, and some steps (like closing) are handled by them.',
  },
  {
    q: 'What does the AI do?',
    a: 'It suggests. The AI summarizes market data and comparable sales and offers ideas for your price, offer, or counter-offer. It does not negotiate for you or make decisions — every term is yours to choose.',
  },
];

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  const answerId = `faq-answer-${index}`;
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button
        className="faq-question"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={answerId}
      >
        <span>{q}</span>
        <span className="faq-chevron" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      <div className="faq-answer" id={answerId} aria-hidden={!open}>
        <p>{a}</p>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function PricingPage({ onBrowseHomes, onListHome }) {
  const heroRef = useRef(null);

  const handlePlanCta = (planKey) => {
    if (planKey === 'pro') {
      // The listing fee is seller-paid and charged at publish — route to the
      // "list your home" flow, not a checkout.
      if (onListHome) onListHome();
      return;
    }
    // Explorer and Buyer Paperwork both start in the buyer home search.
    if (onBrowseHomes) onBrowseHomes();
  };

  return (
    <div className="pricing-page">

      {/* ── Hero ── */}
      <section className="pricing-hero" ref={heroRef}>
        <div className="pricing-hero__bg-grid" aria-hidden="true" />
        <div className="pricing-hero__noise" aria-hidden="true" />

        <div className="pricing-hero__content">
          <div className="pricing-hero__pill">Offers on cutthe6.com homes are free</div>

          <h1 className="pricing-hero__headline">
            Sell your own home —
            <br />
            <em>without a listing commission.</em>
          </h1>

          <p className="pricing-hero__sub">
            One flat software fee to list your home. Buyers make offers on
            cutthe6.com homes for free
            {BUYER_PAPERWORK_LIVE
              ? ', and can prepare paperwork for a home listed anywhere else as part of buying on cutthe6.com.'
              : '.'}
          </p>

          <div className="pricing-hero__stats">
            <div className="hero-stat">
              <span className="hero-stat__num">{LISTING_FEE}</span>
              <span className="hero-stat__label">flat to list</span>
            </div>
            <div className="hero-stat__divider" aria-hidden="true" />
            <div className="hero-stat">
              <span className="hero-stat__num">$0</span>
              <span className="hero-stat__label">offers on our homes</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Cards ── */}
      <section className="pricing-cards-section">
        <div className={`pricing-cards-wrapper pricing-cards-wrapper--${VISIBLE_PLANS.length}`}>
          {VISIBLE_PLANS.map((plan) => (
            <article
              key={plan.key}
              className={`pricing-card pricing-card--${plan.color} ${plan.badge ? 'pricing-card--featured' : ''}`}
              data-testid={`plan-card-${plan.key}`}
            >
              {plan.badge && (
                <div className="pricing-card__badge">{plan.badge}</div>
              )}

              <header className="pricing-card__header">
                <h2 className="pricing-card__name">{plan.name}</h2>
                <p className="pricing-card__tagline">{plan.tagline}</p>
              </header>

              <div className="pricing-card__price-block">
                <div className="pricing-card__amount">
                  {plan.priceText ? (
                    <span className="pricing-card__price-text">{plan.priceText}</span>
                  ) : plan.priceLabel ? (
                    <span className="pricing-card__dollar">{plan.priceLabel}</span>
                  ) : (
                    <>
                      <sup className="pricing-card__currency">$</sup>
                      <span className="pricing-card__dollar">{plan.price.toLocaleString()}</span>
                      <span className="pricing-card__suffix">{plan.priceSuffix}</span>
                    </>
                  )}
                </div>
                {plan.tierNote && (
                  <div className="pricing-card__tier-note">{plan.tierNote}</div>
                )}
                <div className="pricing-card__note">{plan.pricingNote}</div>
                {plan.compareNote && (
                  <div className="pricing-card__compare">{plan.compareNote}</div>
                )}
              </div>

              <ul className="pricing-features-list">
                {plan.features.filter(live).map((f) => (
                  <CheckRow
                    key={f.text}
                    text={f.text}
                    checked={f.yes}
                    featured={f.featured}
                  />
                ))}
              </ul>

              <button
                className={`pricing-cta pricing-cta--${plan.ctaVariant} pricing-cta--${plan.color}`}
                aria-label={`${plan.ctaLabel} — ${plan.name} plan`}
                data-testid={`plan-cta-${plan.key}`}
                onClick={() => handlePlanCta(plan.key)}
              >
                {plan.ctaLabel}
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* ── Savings Calculator ── */}
      <section className="pricing-calculator-section">
        <SavingsCalculator />
      </section>

      {/* ── Comparison Strip ── */}
      <section className="comparison-section">
        <div className="comparison-inner">
          <h2 className="comparison-title">Cut the 6 vs. Traditional Agent</h2>
          <div className="comparison-table" role="table">
            <div className="comparison-row comparison-row--header" role="row">
              <div role="columnheader" />
              <div role="columnheader" className="comparison-col--us">Cut the 6</div>
              <div role="columnheader" className="comparison-col--them">Traditional</div>
            </div>
            {[
              ['Listing-side cost', `${LISTING_FEE} flat (${LISTING_FEE_PREMIUM} at $1M+)`, 'Around 2.5–3% of the sale price, typically'],
              ['Offers on listed homes', '✓ Free on cutthe6.com homes', 'Usually through a buyer\'s agent'],
              ...(BUYER_PAPERWORK_LIVE
                ? [['Paperwork for other homes', '✓ Part of buying on cutthe6.com', 'Included in the commission']]
                : []),
              ['AI help', '✓ Suggestions — you decide', 'Varies by agent'],
              ['Availability', '✓ Use it anytime', 'Varies by agent'],
              ['Who negotiates?', 'You do, directly with the other side', 'Your agent, on your behalf'],
            ].map(([label, us, them]) => (
              <div className="comparison-row" role="row" key={label}>
                <div className="comparison-label" role="rowheader">{label}</div>
                <div className="comparison-col--us" role="cell">{us}</div>
                <div className="comparison-col--them" role="cell">{them}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="pricing-faq">
        <div className="pricing-faq__inner">
          <h2 className="pricing-faq__title">Common questions</h2>
          <div className="faq-list">
            {FAQ.filter(live).map((item, i) => (
              <FAQItem key={item.q} q={item.q} a={item.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="pricing-final-cta">
        <div className="pricing-final-cta__inner">
          <div className="pricing-final-cta__pill">Flat fee, not a percentage</div>
          <h2 className="pricing-final-cta__headline">
            Your next move starts here.
          </h2>
          <p className="pricing-final-cta__sub">
            Skip the listing commission — run your own sale with software built for it.
          </p>
          <div className="pricing-final-cta__buttons">
            <button
              className="pricing-cta pricing-cta--solid pricing-cta--coral pricing-cta--lg"
              onClick={() => handlePlanCta('explorer')}
            >
              Browse Homes
            </button>
            <button
              className="pricing-cta pricing-cta--ghost pricing-cta--lg"
              onClick={() => handlePlanCta('pro')}
            >
              List Your Home →
            </button>
          </div>
        </div>
      </section>

      {/* ── Fine print ── */}
      <footer className="pricing-disclaimer">
        <p data-testid="pricing-disclaimer">{PRICING_DISCLAIMER}</p>
      </footer>
    </div>
  );
}
